import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationsData } from '../../mock';
import { useAuth } from '../../contexts/AuthContext';
import {
  Search,
  Bell,
  Sun,
  Moon,
  ChevronDown,
  LogOut,
  User,
  Settings,
  Menu
} from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Avatar, AvatarFallback } from '../ui/avatar';

interface HeaderProps {
  isDark: boolean;
  setIsDark: (value: boolean) => void;
  sidebarCollapsed: boolean;
  onMobileMenuToggle: () => void;
}

export default function Header({ isDark, setIsDark, sidebarCollapsed, onMobileMenuToggle }: HeaderProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const unreadCount = notificationsData.filter(n => !n.read).length;
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const displayName = user?.firstName && user?.lastName 
    ? `${user.firstName} ${user.lastName}`
    : user?.username || 'Админ';
  
  const displayEmail = user?.email || 'admin@example.com';

  return (
    <header className={cn(
      'h-16 fixed top-0 right-0 z-30 flex items-center justify-between px-6 transition-all duration-300',
      // На мобильном сайдбар — overlay, поэтому хедер на всю ширину (left-0);
      // на десктопе (lg+) смещаем под сайдбар в зависимости от его состояния.
      'left-0',
      sidebarCollapsed ? 'lg:left-20' : 'lg:left-64',
      isDark ? 'bg-[#0F0F17]/90 backdrop-blur-md border-b border-[#2D2D38]' : 'bg-white/85 backdrop-blur-md border-b border-[#EFEAE0]'
    )}
    >
      {/* Mobile menu button */}
      <button
        onClick={onMobileMenuToggle}
        aria-label="Открыть меню навигации"
        className={cn(
          'lg:hidden p-2 rounded-2xl mr-2',
          isDark ? 'text-slate-400 hover:bg-[#1A1A22]' : 'text-slate-600 hover:bg-[#F5F3EF]'
        )}
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Search */}
      <div className="flex-1 max-w-md">
        <div className="relative">
          <Search className={cn(
            'absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4',
            isDark ? 'text-slate-500' : 'text-slate-400'
          )} />
          <Input
            type="text"
            placeholder="Поиск..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={cn(
              'pl-10 h-10 rounded-2xl border-0 focus-visible:ring-2 focus-visible:ring-offset-0',
              isDark
                ? 'bg-[#1A1A22] text-white placeholder:text-slate-500 focus-visible:ring-[#C9A227]/40'
                : 'bg-[#F5F3EF] text-[#111111] placeholder:text-slate-400 focus-visible:ring-[#C9A227]/40'
            )}
          />
        </div>
      </div>

      {/* Right section */}
      <div className="flex items-center gap-2">
        {/* Theme toggle */}
        <button
          onClick={() => setIsDark(!isDark)}
          aria-label={isDark ? 'Включить светлую тему' : 'Включить тёмную тему'}
          className={cn(
            'p-2.5 rounded-2xl transition-all duration-200',
            isDark
              ? 'text-slate-400 hover:bg-[#1A1A22] hover:text-white'
              : 'text-slate-500 hover:bg-[#F5F3EF] hover:text-[#111111]'
          )}
        >
          {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>

        {/* Notifications */}
        <Popover>
          <PopoverTrigger asChild>
            <button
              aria-label="Уведомления"
              className={cn(
              'p-2.5 rounded-2xl transition-all duration-200 relative',
              isDark
                ? 'text-slate-400 hover:bg-[#1A1A22] hover:text-white'
                : 'text-slate-500 hover:bg-[#F5F3EF] hover:text-[#111111]'
            )}>
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center font-medium">
                  {unreadCount}
                </span>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent
            className={cn(
              'w-80 p-0 rounded-2xl shadow-lg',
              isDark ? 'bg-[#1A1A22] border-[#2D2D38]' : 'bg-white border-[#EFEAE0]'
            )}
            align="end"
          >
            <div className={cn(
              'px-4 py-3 border-b',
              isDark ? 'border-[#2D2D38]' : 'border-[#EFEAE0]'
            )}>
              <h3 className={cn(
                'font-semibold',
                isDark ? 'text-white' : 'text-[#111111]'
              )}>Уведомления</h3>
            </div>
            <div className="max-h-72 overflow-y-auto">
              {notificationsData.map((notification) => (
                <div
                  key={notification.id}
                  className={cn(
                    'px-4 py-3 border-b last:border-b-0 transition-colors',
                    isDark ? 'border-[#2D2D38] hover:bg-[#0F0F17]' : 'border-[#EFEAE0] hover:bg-[#F5F3EF]',
                    !notification.read && (isDark ? 'bg-[#C9A227]/10' : 'bg-[#FBF3D9]/40')
                  )}
                >
                  <p className={cn(
                    'text-sm font-medium',
                    isDark ? 'text-white' : 'text-[#111111]'
                  )}>{notification.title}</p>
                  <p className={cn(
                    'text-xs mt-0.5',
                    isDark ? 'text-slate-400' : 'text-slate-500'
                  )}>{notification.message}</p>
                  <p className={cn(
                    'text-xs mt-1',
                    isDark ? 'text-slate-500' : 'text-slate-400'
                  )}>{notification.time}</p>
                </div>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        {/* User dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className={cn(
              'flex items-center gap-2 px-2 py-1.5 rounded-2xl transition-all duration-200',
              isDark
                ? 'hover:bg-[#1A1A22]'
                : 'hover:bg-[#F5F3EF]'
            )}>
              <Avatar className="w-8 h-8">
                <AvatarFallback className="bg-gradient-to-br from-[#F5DC8A] via-[#C9A227] to-[#9C7E1B] text-white text-sm font-bold">
                  {displayName.split(' ').map(n => n[0]).join('').toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="hidden sm:block text-left">
                <p className={cn(
                  'text-sm font-medium leading-none',
                  isDark ? 'text-white' : 'text-[#111111]'
                )}>{displayName}</p>
                <p className={cn(
                  'text-xs mt-0.5',
                  isDark ? 'text-slate-400' : 'text-slate-500'
                )}>Администратор</p>
              </div>
              <ChevronDown className={cn(
                'w-4 h-4 hidden sm:block',
                isDark ? 'text-slate-400' : 'text-slate-500'
              )} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className={cn(
              'w-56 rounded-2xl shadow-lg',
              isDark ? 'bg-[#1A1A22] border-[#2D2D38]' : 'bg-white border-[#EFEAE0]'
            )}
            align="end"
          >
            <div className={cn(
              'px-3 py-2 border-b',
              isDark ? 'border-[#2D2D38]' : 'border-[#EFEAE0]'
            )}>
              <p className={cn(
                'text-sm font-medium',
                isDark ? 'text-white' : 'text-[#111111]'
              )}>{displayName}</p>
              <p className={cn(
                'text-xs',
                isDark ? 'text-slate-400' : 'text-slate-500'
              )}>{displayEmail}</p>
            </div>
            <DropdownMenuItem className={cn(
              'cursor-pointer rounded-xl mx-1 my-1',
              isDark ? 'text-slate-300 focus:bg-[#0F0F17] focus:text-white' : 'text-slate-600 focus:bg-[#F5F3EF]'
            )}>
              <User className="w-4 h-4 mr-2" />
              Профиль
            </DropdownMenuItem>
            <DropdownMenuItem className={cn(
              'cursor-pointer rounded-xl mx-1',
              isDark ? 'text-slate-300 focus:bg-[#0F0F17] focus:text-white' : 'text-slate-600 focus:bg-[#F5F3EF]'
            )}>
              <Settings className="w-4 h-4 mr-2" />
              Настройки
            </DropdownMenuItem>
            <DropdownMenuSeparator className={isDark ? 'bg-[#2D2D38]' : 'bg-[#EFEAE0]'} />
            <DropdownMenuItem
              onClick={handleLogout}
              className={cn(
                'cursor-pointer rounded-xl mx-1 my-1 text-red-500 focus:text-red-500',
                isDark ? 'focus:bg-[#0F0F17]' : 'focus:bg-red-50'
              )}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Выйти
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

